/* eslint-disable import/no-named-as-default-member */
/* eslint-disable import/no-named-as-default */
import React from 'react';
import { createRoot } from 'react-dom/client';

import App from 'App/app.jsx';
import initStore from 'App/initStore';
// eslint-disable-next-line
import registerServiceWorker from 'Utils/PWA';

import AppNotificationMessages from './App/Containers/app-notification-messages.jsx';
import BinarySocket from '_common/base/socket_base';

const is_embedded_page =
    (window as unknown as { __DTRADER_IS_EMBEDDED?: boolean }).__DTRADER_IS_EMBEDDED === true;

if (
    !is_embedded_page &&
    (!!window?.localStorage.getItem?.('debug_service_worker') ||
        !window.location.hostname.startsWith('localhost'))
) {
    registerServiceWorker();
}

const hideHtmlLoader = () => {
    try {
        document.documentElement.style.setProperty('--initial-loader-display', 'none');
    } catch {
        /* ignore */
    }
};

const initApp = async () => {
    // ai.money8gg / manual-trader pattern: boot React immediately — do NOT block on the
    // 10s embed handshake. Auth arrives via postMessage while the app is already mounted.
    const root_store = await initStore(AppNotificationMessages);

    const is_embedded =
        (window as unknown as { __DTRADER_IS_EMBEDDED?: boolean }).__DTRADER_IS_EMBEDDED === true;

    const hasOAuthSession = () => {
        try {
            const raw = sessionStorage.getItem('auth_info');
            if (!raw) return false;
            const info = JSON.parse(raw) as { access_token?: string };
            return !!(info?.access_token && String(info.access_token).length > 0);
        } catch {
            return false;
        }
    };

    let embed_client_init_started = false;
    let embed_client_init_promise: Promise<void> | null = null;
    let embed_reauth_debounce: ReturnType<typeof setTimeout> | null = null;

    const runEmbedClientInit = async () => {
        if (!is_embedded || !hasOAuthSession()) return;
        if (root_store.client.is_logged_in) return;
        if (embed_client_init_promise) return embed_client_init_promise;

        embed_client_init_promise = (async () => {
            embed_client_init_started = true;
            root_store.client.setIsLoggingIn(true);
            try {
                await root_store.client.init();
                if (root_store.client.is_logged_in) {
                    root_store.portfolio.onMount();
                    try {
                        window.parent.postMessage({ type: 'COMPONENTS_READY' }, '*');
                    } catch {
                        /* ignore */
                    }
                }
            } catch (error) {
                embed_client_init_started = false;
                // eslint-disable-next-line no-console
                console.error('[DTrader embed] client.init failed:', error);
            } finally {
                if (!root_store.client.is_logged_in) {
                    root_store.client.setIsLoggingIn(false);
                }
            }
        })();

        try {
            await embed_client_init_promise;
        } finally {
            embed_client_init_promise = null;
        }
    };

    const scheduleEmbedClientInit = () => {
        if (embed_reauth_debounce) clearTimeout(embed_reauth_debounce);
        embed_reauth_debounce = setTimeout(() => {
            embed_reauth_debounce = null;
            void runEmbedClientInit();
        }, 300);
    };

    if (is_embedded) {
        let last_embed_init_loginid = '';
        let embed_reauth_in_flight = false;
        (window as unknown as { __DTRADER_EMBED_REAUTH?: () => Promise<void> }).__DTRADER_EMBED_REAUTH =
            async () => {
                if (embed_reauth_in_flight) {
                    return;
                }
                embed_reauth_in_flight = true;
                try {
                    if (root_store.common.has_error) {
                        root_store.common.setError(false, null);
                    }
                    root_store.common.resetServicesError();

                    if (
                        root_store.client.is_logged_in &&
                        root_store.client.is_authorize &&
                        root_store.common.is_socket_opened
                    ) {
                        try {
                            BinarySocket.send({ balance: 1, subscribe: 1 });
                            BinarySocket.sendBuffered();
                        } catch {
                            /* ignore */
                        }
                        return;
                    }

                    if (
                        root_store.client.is_logged_in &&
                        typeof root_store.client.reconnectEmbedOAuthSocket === 'function'
                    ) {
                        await root_store.client.reconnectEmbedOAuthSocket();
                        return;
                    }
                    const loginid =
                        sessionStorage.getItem('active_loginid') ||
                        localStorage.getItem('active_loginid') ||
                        '';
                    scheduleEmbedClientInit();
                    last_embed_init_loginid = loginid;
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('[DTrader embed] reauth failed:', err);
                } finally {
                    embed_reauth_in_flight = false;
                }
            };

        const embedReady: Promise<unknown> =
            (window as unknown as { __DTRADER_EMBED_AUTH_READY?: Promise<unknown> })
                .__DTRADER_EMBED_AUTH_READY ?? Promise.resolve({ embedded: true });

        void embedReady.then(() => {
            if (hasOAuthSession() && !root_store.client.is_logged_in) {
                scheduleEmbedClientInit();
            }
        });

        window.addEventListener('dtrader-embed-auth', () => {
            if (!root_store.client.is_logged_in) {
                scheduleEmbedClientInit();
            }
        });

        if (hasOAuthSession()) {
            scheduleEmbedClientInit();
        }

        if (
            (window as unknown as { __DTRADER_EMBED_AUTH_LATE?: boolean }).__DTRADER_EMBED_AUTH_LATE
        ) {
            scheduleEmbedClientInit();
            try {
                delete (window as unknown as { __DTRADER_EMBED_AUTH_LATE?: boolean })
                    .__DTRADER_EMBED_AUTH_LATE;
            } catch {
                /* ignore */
            }
        }
    }

    const wrapper = document.getElementById('derivatives_trader');
    if (wrapper) {
        const root = createRoot(wrapper);
        root.render(<App root_store={root_store} />);
        hideHtmlLoader();
    }
};

initApp();
